"use client"

import { APIProvider } from "@vis.gl/react-google-maps"
import { getWindow } from "utils-lib/getWindow"

const ssrWindow = getWindow()

/**
 * GoogleMapsProvider - A wrapper around @vis.gl/react-google-maps APIProvider
 * that provides SSR compatibility and proper configuration for the new Places API.
 *
 * This component should wrap your application (or the layout containing search functionality)
 * to provide access to Google Maps libraries including the new Places API.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components that need access to Google Maps
 * @param {string} props.apiKey - Google Maps API key (defaults to env variable)
 * @param {string} props.region - Region code for Places API (defaults to 'US')
 * @param {string} props.language - Language code (optional)
 *
 * @example
 * ```jsx
 * <GoogleMapsProvider apiKey={process.env.NEXT_PUBLIC_GOOGLE_API_KEY}>
 *   <YourAppContent />
 * </GoogleMapsProvider>
 * ```
 */
export const GoogleMapsProvider = ({
  children,
  apiKey,
  region = "US",
  language,
}) => {
  // Determine the API key from props or environment variables
  const googleApiKey =
    apiKey ||
    (typeof ssrWindow !== "undefined"
      ? process.env.NEXT_PUBLIC_GOOGLE_API_KEY ||
        process.env.REACT_APP_GOOGLE_API_KEY
      : "")

  // Don't render APIProvider on server-side to prevent "window is not defined" errors
  if (typeof ssrWindow === "undefined" || !ssrWindow.document) {
    return <>{children}</>
  }

  // If no API key is available, just render children
  // (for backward compatibility during migration)
  if (!googleApiKey) {
    console.warn(
      "GoogleMapsProvider: No API key provided. Google Maps features will not work.",
    )
    return <>{children}</>
  }

  return (
    <APIProvider
      apiKey={googleApiKey}
      region={region}
      language={language}
      libraries={["geometry", "places"]}
      solutionChannel="GMP_GCC_reactgooglemaps_v1"
    >
      {children}
    </APIProvider>
  )
}

export default GoogleMapsProvider
